import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { PRODUCT_TOKEN } from '@rsos/sinatra/src/actions/types';
import { checkStatus, parseJSON } from '@rsos/utils';
import { getAPIHost } from '@rsos/utils/metaTags';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import fetchRetryWithADR from '../components/AdditionalData/fetchRetryWithADR';
import { default as mockMafid } from '../components/AdditionalData/fixtures/rade-mafid.4.1.0-fixed1.fixture';
import mockedTel from '../components/AdditionalData/fixtures/rade-tel.4.1.0.fixture';
import { getProductToken } from '../utils/getProductToken';
import {
  CLEAR_RAD_SESSION,
  FETCH_RAD_SESSION_START,
  FETCH_RAD_SESSION_SUCCESS,
  FETCH_RAD_SESSION_FAILED,
} from './actionTypes';

const mock = false;

// NOTE that medicalert is originally set to `PROFILE_INFO` in
// the database, this predates the naming convention followed by the
// regional capabilities (UK, CA). The display name for PROFILE_INFO
// is set to MedicAlert but the value continues to be used.
export const MEDICALERT = 'medicalert';
export const MEDICALERT_UK = 'medicalert_uk';
export const MEDICALERT_CA = 'medicalert_ca';
export const PROFILE_INFO = 'profile_info';

const MEDICALERT_CAPABILITIES = [MEDICALERT, MEDICALERT_UK, MEDICALERT_CA];

class MedicAlertConfigError extends Error {
  constructor() {
    super(`Only one type of MedicAlert capability should be on at a time.`);
    this.name = 'MedicAlertConfigError';
  }
}

const defaultOptions = {
  attributes: {
    protocol: 'rad-e',
    scheme: 'tel',
    context: { parent_data_uri: '' },
  },
  silentUpdate: false,
};

export const fetchRADSessionStart = () => ({
  type: FETCH_RAD_SESSION_START,
});

/**
 * Action creator for storing RAD Session data.
 * @param {Object} radSession - response object from RAD Session API request
 * @param {?string} [id=null] - ID associated with the phone number the user is viewing
 */
export const fetchRADSessionSuccess = (radSession, id = null) => {
  return {
    type: FETCH_RAD_SESSION_SUCCESS,
    payload: radSession, // for 911inform specific in 3.4
    id,
  };
};

export const fetchRADSessionFailed = error => ({
  type: FETCH_RAD_SESSION_FAILED,
  error,
});

export const clearRADSession = () => ({
  type: CLEAR_RAD_SESSION,
});

const findMedicAlertCapabilities = caps => {
  return Object.entries(caps)
    .filter(cap => MEDICALERT_CAPABILITIES.includes(cap.name))
    .map(cap => cap.name);
};

const findUserPermissions = user => {
  const userPermissions = selectUserPermissionsByApp(user, 'capstone') || [];
  return userPermissions.map(permission => {
    if (permission.toLowerCase() === PROFILE_INFO) {
      return MEDICALERT;
    }
    return permission.replace(/^_/, '').toLowerCase();
  });
};

const fetchEmergencySession = (query, options = {}) => (dispatch, store) => {
  const { sinatra, psaps } = store();
  const { user = {} } = sinatra;

  const permissions = findUserPermissions(user);

  const configuredOptions = {
    ...defaultOptions,
    ...options,
  };

  let cleanQuery = `${query}`;
  if (cleanQuery.startsWith('/')) {
    const queryParts = cleanQuery.split(' ');
    if (queryParts.length > 1) {
      cleanQuery = queryParts.slice(1).join(' ');
    }
  }
  let { scheme } = configuredOptions.attributes;

  // Standalone searches using /maf end up here. The default scheme is `maf-id`.
  // We do additional checks to see if any of the regional capabilities are on.
  // If they are, the corresponding search scheme is overridden here. The check
  // for the permission is also done, to respect the ECC admin's configuration.
  // To learn more on why these are separate schemes see MC-17621.
  if (scheme === 'maf-id') {
    const caps = findMedicAlertCapabilities(
      psaps.currentPSAP.activeCapabilities,
    );

    if (caps.length > 1) {
      // there's more than 1 MA capability, fallback to "US" and report it.
      scheme = 'maf-id';
      captureExceptionWithScope(new MedicAlertConfigError(), {
        ecc: psaps?.currentPSAP?.account_id,
      });
    } else if (
      caps.includes(MEDICALERT_UK) &&
      permissions.includes(MEDICALERT_UK)
    ) {
      scheme = 'maf-id-uk';
    } else if (
      caps.includes(MEDICALERT_CA && permissions.includes(MEDICALERT_CA))
    ) {
      scheme = 'maf-id-ca';
    } else if (caps.includes(MEDICALERT) && permissions.includes(MEDICALERT)) {
      scheme = 'maf-id';
    }
  }

  let { productToken: token } = getProductToken(sinatra);
  if (!token) {
    // At time of writing this, there's two ways to read the product token.
    // This is the fallback ported from existing code.
    token = localStorage.getItem(PRODUCT_TOKEN);
  }
  return fetchRetryWithADR(
    `${getAPIHost()}/v2/emergency-data/session`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `${scheme}://${cleanQuery}`,
        meta: true,
      }),
    },
    scheme,
  )
    .then(checkStatus)
    .then(parseJSON)
    .then(response => {
      let payload = response;
      if (mock) {
        payload = scheme === 'maf-id' ? mockMafid : mockedTel;
      }

      const allowedPayload = {};
      for (const orgName in payload) {
        if (!permissions.includes(orgName)) {
          continue;
        }

        allowedPayload[orgName] = payload[orgName];
      }

      if (options.silentUpdate) {
        // suppress dispatch success
        return { payload: allowedPayload };
      }
      return dispatch(fetchRADSessionSuccess(allowedPayload, cleanQuery));
    })
    .catch(error => {
      throw dispatch(fetchRADSessionFailed(error));
    });
};

export default fetchEmergencySession;
